import React, { useEffect, useState } from 'react'

import {
  Form,
  Section,
  DataType,
  FormOptions,
  SelectOption,
  DecimalScale,
  useNotify
} from '@globaleris/ui.library'
import { useTranslation } from 'react-i18next'
import { AllocationModel, CapacityModel } from '../capacity.model'
import {
  fetchLogisticElementsToItems,
  getCapacityItem,
  patchCapacityItem,
  updateLockState
} from '../capacity.service'

import {
  EnumServicio,
  EnumTipoProductoCapacidad,
  EnumTipoNaturaleza,
  EnumBloqueoCapacidad
} from '../../../api'
import { enumToSelectOptionArray, enumValueToKey } from '../../../helpers'
import { styles } from './editNoBundle.styles'

import { useHistory } from 'react-router-dom'
import { NonAuthRoutes } from '../../../model'
import { makeStyles, useTheme } from '@material-ui/core'
import { useConfirmationDialog } from '../../../hooks/useConfirmationDialog'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import Page from '../../../components/page/page'
import { Allocations } from '../forms'
import { useHttpError } from '../../../hooks/useHttpError'
import { Number } from '@globaleris/ui.library/src/lib'
import { AddendumsCard } from './addendum/addendumCard'

export const EditCapacityNoBundlePage: React.FC<any> = props => {
  const [t] = useTranslation(['capacity', 'enums'])
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const [logisticElements, setLogisticElements] = useState<SelectOption[]>([])
  const [capacity, setCapacity] = useState<CapacityModel | undefined>(undefined)
  const [allocations, setAllocations] = useState<AllocationModel[] | undefined>(undefined)
  const [canEditAllocations, setCanEditAllocations] = useState<boolean>(false)
  let history = useHistory()
  const { info } = useNotify()

  const { showError, clearError, errorProps } = useHttpError()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  const [duration, setDuration] = useState<any>(false)

  useEffect(() => {
    const fetchCapacity = async (id: number) => {
      await getCapacityItem(id).then(response => {
        if (response.success) {
          clearError()
          setCapacity(response.data)
        } else {
          showError({
            title: t('capacity:forms.edit.title'),
            errorCode: response.error!.code,
            actions: [
              {
                label: t('capacity:forms.edit.mainActions.back'),
                onClick: () => {
                  history.push(NonAuthRoutes.capacity)
                },
                default: true
              }
            ],
            onRefresh: () => fetchCapacity(id)
          })
        }
      })
    }

    if (props.location.state) {
      fetchCapacity(props.location.state.idCapacity)
    } else {
      history.push(NonAuthRoutes.capacity)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (capacity) {
      capacity.lockState !== EnumBloqueoCapacidad.BloqueadoUsuario && setCanEditAllocations(true)
      setAllocations(capacity.allocations)
    }
  }, [capacity])

  useEffect(() => {
    capacity && getLogisticElements([enumValueToKey(EnumServicio, capacity?.service)])
  }, [capacity])

  useEffect(() => {
    if (capacity) {
      setDuration(capacity.duration)
    }
  }, [capacity])

  //#region Form definition

  const options: FormOptions = {
    elevation: 2,
    columns: 4,
    onSave: async (data: any) => {
      if (data) {
        onFormSaveHandler(data)
      } else {
        history.push(NonAuthRoutes.capacity)
      }
      return true
    }
  }

  const sections: Section[] = [
    {
      inputs: [
        {
          name: 'code',
          label: t('capacity:forms.edit.inputs.code'),
          type: DataType.TEXT,
          required: true
        },
        {
          name: 'service',
          label: t('capacity:forms.edit.inputs.service'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumServicio, 'enums:EnumServicio'),
          onChange: async (data: any) => {
            getLogisticElements([enumValueToKey(EnumServicio, data)])
          },
          required: true
        },
        {
          name: 'logisticElement.id',
          label: t('capacity:forms.edit.inputs.logisticElement'),
          type: DataType.SELECT,
          selectOptions: logisticElements,
          required: true,
          helpText: t('capacity:forms.edit.helpTexts.logisticElement')
        },
        {
          name: 'duration',
          label: t('capacity:forms.edit.inputs.duration'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(
            EnumTipoProductoCapacidad,
            'enums:EnumTipoProductoCapacidad'
          ),
          required: true,
          onChange: async (durationSelected: any, formData) => {
            setDuration(durationSelected),
              setCapacity(prevState => ({ ...prevState, hoursQty: undefined }))
          }
        },
        {
          name: 'nature',
          label: t('capacity:forms.edit.inputs.nature'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumTipoNaturaleza, 'enums:EnumTipoNaturaleza'),
          required: true
        },
        {
          name: 'initDate',
          label: t('capacity:forms.edit.inputs.initDate'),
          type: DataType.DATE,
          required: true
        },
        {
          name: 'endDate',
          label: t('capacity:forms.edit.inputs.endDate'),
          type: DataType.DATE,
          required: true
        },
        {
          name: 'signDate',
          label: t('capacity:forms.edit.inputs.signDate'),
          type: DataType.DATE,

          required: true
        },
        {
          name: 'capacity',
          label: t('capacity:forms.edit.inputs.capacity'),
          type: DataType.NUMBER,

          required: true
        },
        {
          name: 'premium',
          label: t('capacity:forms.edit.inputs.premium'),
          type: DataType.NUMBER,

          decimalScale: DecimalScale.CURRENCY
        },
        {
          name: 'rawMaterial',
          label: t('capacity:forms.edit.inputs.rawMaterial'),
          type: DataType.SWITCH
        },
        {
          name: 'lock',
          label: t('capacity:forms.edit.inputs.lock'),
          type: DataType.SWITCH,
          defaultValue: false,

          onChange: async (data: any) => {
            onLockChangeActionHandler(data)
          }
        },
        {
          name: 'hoursQty',
          label: t('capacity:forms.add.inputs.hoursQty'),
          maxLength: 200,
          type: DataType.NUMBER,
          display: duration == EnumTipoProductoCapacidad.Intradiario,
          minValue: 1,
          maxValue: 24,
          customDecimalScale: 0,
          errorText: t('capacity:notifications.error.hoursQty')
        }
      ]
    }
  ]

  //#endregion

  //#region Handlers

  const onFormSaveHandler = async (data: CapacityModel): Promise<boolean> => {
    return await patchCapacityItem(data).then(success => {
      if (success) {
        info(t('capacity:forms.edit.notifications.success.edit'))
        history.push(NonAuthRoutes.capacity)
        return true
      }
      info(t('capacity:forms.edit.notifications.error.edit'))
      return false
    })
  }

  const onLockChangeHandler = (state: EnumBloqueoCapacidad) => {
    state === EnumBloqueoCapacidad.Libre
      ? setCanEditAllocations(true)
      : setCanEditAllocations(false)
  }

  const onLockChangeActionHandler = (data: boolean) => {
    data ? onLockActionHandler() : onUnlockActionHandler()
  }

  const onLockActionHandler = async () => {
    getConfirmation(ConfirmationDialogs.LOCK).then(async () => {
      capacity &&
        capacity.id &&
        (await updateLockState(capacity.id, EnumBloqueoCapacidad.BloqueadoUsuario).then(success => {
          if (success) {
            info(t('capacity:forms.edit.notifications.success.lock'))
            onLockChangeHandler(EnumBloqueoCapacidad.BloqueadoUsuario)
          }
        }))
      closeConfirmation()
    })
  }

  const onUnlockActionHandler = async () => {
    getConfirmation(ConfirmationDialogs.UNLOCK).then(async () => {
      capacity &&
        capacity.id &&
        (await updateLockState(capacity.id, EnumBloqueoCapacidad.Libre).then(success => {
          if (success) {
            info(t('capacity:forms.edit.notifications.success.unlock'))
            onLockChangeHandler(EnumBloqueoCapacidad.Libre)
          }
        }))
      closeConfirmation()
    })
  }

  //#endregion

  const getLogisticElements = async (serviceTypes: EnumServicio[]) => {
    fetchLogisticElementsToItems(serviceTypes).then(setLogisticElements)
  }

  return (
    <Page fullWidth={false} error={errorProps}>
      <div className={classes.form}>
        <Form
          title={capacity && capacity.code}
          sections={sections}
          data={capacity}
          options={options}
        />
      </div>
      <Allocations canSend={false} canEdit={canEditAllocations} data={allocations}></Allocations>
      <AddendumsCard addendums={capacity?.addendums} />
    </Page>
  )
}
